import React, { ReactNode, useState, useEffect } from 'react';
import { getUserLikes } from '@/services/likes.api';
import { setLikedVideos } from '@/features/videos/videosSlice';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Video, Heart, Plus, Radio, Box, ListMusic, ChevronDown, ChevronRight, Menu, Disc3, Compass } from 'lucide-react';
import GlobalPlayer from '@/features/player/components/GlobalPlayer';
import UnverifiedEmailBanner from './UnverifiedEmailBanner';
import { useAuth } from '@/features/auth/AuthContext';
import { useAppDispatch, useAppSelector } from '@/store/store';
import { getFollowedChannels, selectFollowedChannels } from '@/features/channels/channelsSlice';
import { Crate, getCrates } from '@/services/crates.api';
import { DjSet, getSets } from '@/services/sets.api';
import { useIsTouchDevice } from '@/hooks/useIsTouchDevice';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [cratesOpen, setCratesOpen] = useState(true);
  const [setsOpen, setSetsOpen] = useState(true);
  const [crates, setCrates] = useState<Crate[]>([]);
  const [sets, setSets] = useState<DjSet[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    let cancelled = false;
    const loadLikedIds = async () => {
      try {
        const ids: string[] = [];
        let offset = 0;
        let hasMore = true;
        while (hasMore) {
          const response = await getUserLikes({ limit: 100, offset });
          response.likes.forEach((item: any) => {
            if (item.video?.id) ids.push(item.video.id);
          });
          hasMore = response.pagination.hasMore;
          offset += 100;
        }
        if (!cancelled) dispatch(setLikedVideos(ids));
      } catch {
        console.error('Failed to load liked ids');
      }
    };
    loadLikedIds();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);
  const { logout } = useAuth();
  const followedChannels = useAppSelector(selectFollowedChannels);
  const isTouch = useIsTouchDevice();

  useEffect(() => {
    dispatch(getFollowedChannels());
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    getCrates()
      .then(result => { if (!cancelled) setCrates(result); })
      .catch(() => undefined);
    getSets()
      .then(result => { if (!cancelled) setSets(result); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [location.pathname]);

  const closeDrawer = () => setDrawerOpen(false);

  const handleLogout = () => {
    closeDrawer();
    logout();
    navigate('/login');
  };

  const handleFollowNewChannel = () => {
    closeDrawer();
    navigate('/channels?tab=search');
  };

  const sidebarContent = (
    <>
      <div className="p-6 border-b border-meradio-light dark:border-border-dark">
            <Link to="/" onClick={closeDrawer} className="block">
              <h1 className="text-2xl font-bold gradient-text">Meradio</h1>
              <p className="text-sm text-meradio-dark-light dark:text-text-dark-secondary mt-1">Music Discovery</p>
            </Link>
          </div>

          <nav className="flex-1 overflow-y-auto p-4 pb-32 md:pb-28 space-y-1">
            <Link
              to="/collections"
              onClick={closeDrawer}
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                location.pathname === '/collections'
                  ? 'bg-meradio-primary text-white shadow-lg'
                  : 'text-meradio-dark dark:text-text-dark-secondary hover:bg-meradio-primary hover:text-white'
              }`}
            >
              <Heart className="w-5 h-5" />
              <span>My collection</span>
            </Link>

            <Link
              to="/videos"
              onClick={closeDrawer}
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                location.pathname === '/videos'
                  ? 'bg-meradio-primary text-white shadow-lg'
                  : 'text-meradio-dark dark:text-text-dark-secondary hover:bg-meradio-primary hover:text-white'
              }`}
            >
              <Video className="w-5 h-5" />
              <span>All videos</span>
            </Link>

            <Link
              to="/channels"
              onClick={closeDrawer}
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                location.pathname === '/channels'
                  ? 'bg-meradio-primary text-white shadow-lg'
                  : 'text-meradio-dark dark:text-text-dark-secondary hover:bg-meradio-primary hover:text-white'
              }`}
            >
              <Radio className="w-5 h-5" />
              <span>All channels</span>
            </Link>

            <Link
              to="/discover"
              onClick={closeDrawer}
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                location.pathname === '/discover'
                  ? 'bg-meradio-primary text-white shadow-lg'
                  : 'text-meradio-dark dark:text-text-dark-secondary hover:bg-meradio-primary hover:text-white'
              }`}
            >
              <Compass className="w-5 h-5" />
              <span>Discover</span>
            </Link>

            {!isTouch && (
              <Link
                to="/decks"
                onClick={closeDrawer}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                  location.pathname === '/decks'
                    ? 'bg-meradio-primary text-white shadow-lg'
                    : 'text-meradio-dark dark:text-text-dark-secondary hover:bg-meradio-primary hover:text-white'
                }`}
              >
                <Disc3 className="w-5 h-5" />
                <span>DJ Mode</span>
              </Link>
            )}

            <div className="pt-2">
              <div className="flex items-center justify-between px-2">
                <button
                  onClick={() => setCratesOpen(prev => !prev)}
                  aria-expanded={cratesOpen}
                  className="flex items-center gap-2 px-2 py-2 text-xs font-semibold uppercase tracking-wider text-meradio-dark-light dark:text-text-dark-tertiary hover:text-meradio-primary transition-colors"
                >
                  {cratesOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <Box className="w-4 h-4" />
                  <span>Crates</span>
                </button>
                <Link
                  to="/crates"
                  onClick={closeDrawer}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-meradio-primary hover:text-meradio-primary-dark transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  New
                </Link>
              </div>
              {cratesOpen && (
                <div className="space-y-0.5 mt-1">
                  {crates.length === 0 ? (
                    <p className="px-4 py-2 text-xs text-meradio-dark-light dark:text-text-dark-tertiary">
                      No crates yet
                    </p>
                  ) : (
                    crates.map((crate) => (
                      <Link
                        key={crate.id}
                        to={`/crates/${crate.id}`}
                        onClick={closeDrawer}
                        className={`flex items-center justify-between px-4 py-2 rounded-lg text-sm transition-all duration-200 ${
                          location.pathname === `/crates/${crate.id}`
                            ? 'bg-meradio-primary text-white shadow'
                            : 'text-meradio-dark dark:text-text-dark-secondary hover:bg-meradio-primary hover:text-white'
                        }`}
                        title={crate.name}
                      >
                        <span className="truncate">{crate.name}</span>
                        <span className="text-xs tabular-nums opacity-70 flex-shrink-0 ml-2">
                          {crate.videoCount}/30
                        </span>
                      </Link>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="pt-2">
              <div className="flex items-center justify-between px-2">
                <button
                  onClick={() => setSetsOpen(prev => !prev)}
                  aria-expanded={setsOpen}
                  className="flex items-center gap-2 px-2 py-2 text-xs font-semibold uppercase tracking-wider text-meradio-dark-light dark:text-text-dark-tertiary hover:text-meradio-primary transition-colors"
                >
                  {setsOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <ListMusic className="w-4 h-4" />
                  <span>Sets</span>
                </button>
                <Link
                  to="/sets"
                  onClick={closeDrawer}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-meradio-primary hover:text-meradio-primary-dark transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  New
                </Link>
              </div>
              {setsOpen && (
                <div className="space-y-0.5 mt-1">
                  {sets.length === 0 ? (
                    <p className="px-4 py-2 text-xs text-meradio-dark-light dark:text-text-dark-tertiary">
                      No sets yet
                    </p>
                  ) : (
                    sets.map((set) => (
                      <Link
                        key={set.id}
                        to={`/sets/${set.id}`}
                        onClick={closeDrawer}
                        className={`flex items-center px-4 py-2 rounded-lg text-sm transition-all duration-200 ${
                          location.pathname === `/sets/${set.id}`
                            ? 'bg-meradio-primary text-white shadow'
                            : 'text-meradio-dark dark:text-text-dark-secondary hover:bg-meradio-primary hover:text-white'
                        }`}
                        title={set.name}
                      >
                        <span className="truncate">{set.name}</span>
                      </Link>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="h-px bg-meradio-light dark:bg-border-dark my-4" />

            <div className="space-y-1">
              {followedChannels.map((channel) => (
                <Link
                  key={channel.channelId}
                  to={`/channels/${channel.channelId}`}
                  onClick={closeDrawer}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                    location.pathname === `/channels/${channel.channelId}`
                      ? 'bg-meradio-primary text-white shadow-lg'
                      : 'text-meradio-dark dark:text-text-dark-secondary hover:bg-meradio-primary hover:text-white'
                  }`}
                  title={channel.name}
                >
                  <div className="w-10 h-10 flex-shrink-0">
                    {channel.thumbnailUrl ? (
                      <img
                        src={channel.thumbnailUrl}
                        alt={channel.name}
                        className="w-full h-full rounded-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(channel.name)}&size=40&background=FF6B35&color=ffffff&format=png&rounded=true`;
                        }}
                      />
                    ) : (
                      <div className="w-full h-full rounded-full bg-gradient-to-br from-meradio-primary to-meradio-secondary flex items-center justify-center">
                        <span className="text-white text-sm font-bold">
                          {channel.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                  <span className="truncate">{channel.name}</span>
                </Link>
              ))}
            </div>

            <div className="h-px bg-meradio-light dark:bg-border-dark my-4" />

            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 text-meradio-dark dark:text-text-dark-secondary hover:bg-meradio-primary hover:text-white group"
            >
              <LogOut className="w-5 h-5" />
              <span>Log out</span>
            </button>
          </nav>

          <div className="p-4 border-t border-meradio-light dark:border-border-dark">
            <button
              onClick={handleFollowNewChannel}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 text-meradio-dark dark:text-text-dark-secondary hover:bg-meradio-primary hover:text-white"
            >
              <Plus className="w-5 h-5" />
              <span>Follow new channel</span>
            </button>
          </div>
    </>
  );

  return (
    <div className="flex flex-col h-screen bg-meradio-cream dark:bg-bg-dark-primary">
      <UnverifiedEmailBanner />

      <div className="md:hidden flex items-center justify-between px-4 py-3 bg-white dark:bg-bg-dark-tertiary border-b border-meradio-light dark:border-border-dark shadow-sm">
        <Link to="/" className="block">
          <h1 className="text-xl font-bold gradient-text">Meradio</h1>
        </Link>
        <button
          onClick={() => setDrawerOpen(true)}
          aria-label="Open navigation menu"
          aria-expanded={drawerOpen}
          className="p-2 rounded-lg text-meradio-dark dark:text-text-dark-secondary hover:bg-meradio-primary hover:text-white transition-colors"
        >
          <Menu className="w-6 h-6" />
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <aside className="hidden md:flex w-64 bg-white dark:bg-bg-dark-tertiary border-r border-meradio-light dark:border-border-dark shadow-sm flex-col">
          {sidebarContent}
        </aside>

        {drawerOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/50 z-40 md:hidden"
              onClick={closeDrawer}
              aria-hidden="true"
            />
            <aside className="fixed inset-y-0 left-0 w-72 z-50 flex flex-col bg-white dark:bg-bg-dark-tertiary border-r border-meradio-light dark:border-border-dark shadow-2xl md:hidden">
              {sidebarContent}
            </aside>
          </>
        )}

        <main className="flex-1 overflow-y-auto pb-32 md:pb-24">
          {children}
        </main>
      </div>

      <GlobalPlayer />
    </div>
  );
};

export default Layout;
